// Daily traffic sparkline + detailed table, extracted from AdminOverview to keep it small.
import { useState } from "react";

const PAGE_SIZE = 15;

export function DailyHistoryChart({ history }) {
  const [page, setPage] = useState(0);
  const rowsDesc = [...history.daily].reverse();
  const pageCount = Math.max(1, Math.ceil(rowsDesc.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = rowsDesc.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="mt-16" data-testid="admin-history">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="gv-label">Daily traffic</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-3 text-[#1A1F24]">
            Visits since {history.from ? new Date(history.from).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "launch"}
          </h2>
        </div>
        <div className="flex gap-8 text-right">
          <div>
            <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">Total visits</div>
            <div className="font-serif text-3xl text-[#1A1F24]" data-testid="history-total-visits">{(history.total_visits || 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">Unique</div>
            <div className="font-serif text-3xl text-[#C5A059]" data-testid="history-total-unique">{(history.total_unique || 0).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {history.daily.length === 0 ? (
        <p className="mt-6 text-sm text-[#5C6670] italic" data-testid="no-history">
          Historical tracking starts on 30 June 2026. Once the first visit lands, a day-by-day breakdown appears here.
        </p>
      ) : (
        <>
          <div className="mt-6 bg-white border border-[#E5E5E5] p-6">
            <div className="flex items-end gap-[3px] h-32" data-testid="history-sparkline">
              {(() => {
                const max = Math.max(1, ...history.daily.map((d) => d.visits));
                return history.daily.map((d) => {
                  const h = Math.max(2, Math.round((d.visits / max) * 100));
                  return (
                    <div
                      key={d.date}
                      title={`${d.date}: ${d.visits} visits · ${d.unique} unique`}
                      className="flex-1 min-w-[2px] bg-[#C5A059]/70 hover:bg-[#C5A059] transition-colors"
                      style={{ height: `${h}%` }}
                    />
                  );
                });
              })()}
            </div>
            <div className="flex justify-between text-[0.6rem] tracking-[0.2em] uppercase text-[#B8BCC2] mt-2">
              <span>{history.daily[0]?.date}</span>
              <span>{history.daily[history.daily.length - 1]?.date}</span>
            </div>
          </div>

          <div className="mt-6 bg-white border border-[#E5E5E5] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-3 font-normal">Date</th>
                  <th className="text-right px-4 py-3 font-normal">Visits</th>
                  <th className="text-right px-4 py-3 font-normal">Unique</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((d) => (
                  <tr key={d.date} className="border-b border-[#F0F0F0]" data-testid={`history-row-${d.date}`}>
                    <td className="px-4 py-2 text-[#1A1F24]">
                      {new Date(d.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{d.visits.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-[#C5A059] tabular-nums">{d.unique.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5] text-xs" data-testid="history-pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#5C6670]"
                  data-testid="history-page-prev"
                >
                  ← Newer
                </button>
                <span className="tracking-[0.2em] uppercase text-[#B8BCC2]" data-testid="history-page-indicator">
                  Page {currentPage + 1} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={currentPage >= pageCount - 1}
                  className="tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059] disabled:opacity-30 disabled:hover:text-[#5C6670]"
                  data-testid="history-page-next"
                >
                  Older →
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
